/* eslint
     no-inner-declarations: "warn",
     @typescript-eslint/no-shadow: "warn",
     no-restricted-syntax: ["warn", "ForOfStatement"],
*/

import { element, globalEvent } from '@shared/easyfy.core';
import {
  show,
  hide
} from '@shared/easyfy.utils';
import { invoke } from '@shared/easyfy.decorators';
import easyfyToaster, { MessageSeverity } from '@shared/easyfy.toaster';
import { IEasyfyEvent } from '@shared/easyfy.events/easyfy.eventdata';
import Events from '@shared/easyfy.events/easyfy.eventhandler';
import { AddToCartState } from '@shared/easyfy.enums';
import favouriteService from '../services/favourite.service';
import { MedsEventNames } from './eventMappers/dataMapper.ga4';
import { setMyPagesMenuState } from './myPagesMenuState';

namespace Meds.Modules {
  @invoke
  export class Favourites {
    static currentMutationObserver: MutationObserver;

    static animationTime: number = 500;

    onInit() {

      globalEvent({
        type: 'click',
        event: {
          action: 'favourite-click',
          callback: Favourites.favouriteClick
        }
      });
    }

    static favouriteClick = async (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      const myPagesMenu = element('#top-menu-mypages') as HTMLElement;
      if (myPagesMenu?.dataset.authenticated !== 'true') {
        const myPagesBtn = element('[data-container="header-mypages"]') as HTMLElement;
        myPagesBtn?.click();
        setMyPagesMenuState(true);
        return;
      }

      const targetElement: HTMLElement = e.target as HTMLElement;
      const productId = targetElement.getAttribute('data-product-favourite-id');
      const isActive = targetElement.classList.contains('icon-favourite-active');
      const request: favouriteService.UpdateFavouriteRequest =
      {
        productId,
        add: !isActive // if isActive not exists we should add
      };

      /* Change element state to 'processing'. */
      targetElement.dataset.state = AddToCartState.Processing;
      targetElement.setAttribute('disabled', 'disabled');
      targetElement.classList.remove('icon-favourite', 'icon-favourite-active');

      if (targetElement.querySelectorAll('.state').length === 0) {
        targetElement
          .appendChild(document.createElement('div'))
          .classList.add('state');
      }

      let favouriteResponse;
      favouriteService
        .UpdateFavourite(request)
        .then((response) => {
          favouriteResponse = response;
          if (favouriteResponse.data.success === true) {
            targetElement.dataset.state = AddToCartState.Success;

            if (request.add) {
              const parent: HTMLElement = targetElement.closest(
                '[data-product-event-id]'
              ) as HTMLElement;

              if (parent) {
                const event: IEasyfyEvent = {
                  eventName: MedsEventNames.AddToWishlist,
                  data: {
                    productIdentifier: parent.getAttribute('data-product-event-id'),
                    quantity: 1
                  }
                };
                Events.EasyfyEventHandler.RunEvent(event);
              }
            }
          }
          else {
            targetElement.dataset.state = AddToCartState.Fail;
            easyfyToaster.toaster(MessageSeverity.error, 'Favoriten kunde inte sparas, försök igen');
          }
        })
        .catch(() => {
          targetElement.dataset.state = AddToCartState.Fail;
          easyfyToaster.toaster(MessageSeverity.error, 'Favoriten kunde inte sparas, försök igen');
        }).finally(() => {

          if (favouriteResponse.data.success === true) {
            Favourites.renderFavourites(favouriteResponse.data.favouritesCount, favouriteResponse.data.favouritesProductId);
          }

          setTimeout(() => {
            /* Remove element state. */
            targetElement.removeAttribute('data-state');
            targetElement.removeAttribute('disabled');
            targetElement.querySelector('.state').remove();

            if (favouriteResponse.data.success === true) {
              const favoriteClass = isActive ? 'icon-favourite' : 'icon-favourite-active';
              if (isActive === true) {
                // remove all since the favourite scan only adds, never removes
                const targetElements = document.querySelectorAll(`[data-product-favourite-id="${productId}"]`);
                targetElements.forEach(product => {
                  product.classList.remove('icon-favourite-active');
                  product.classList.add(favoriteClass);
                });
              } else {
                targetElement.classList.add(favoriteClass);
              }

            } else {
              // If update failed, add the same favourite class based on shouldAdd flag
              const oppositeFavoriteClass = isActive ? 'icon-favourite-active' : 'icon-favourite';
              targetElement.classList.add(oppositeFavoriteClass);
            }
          }, Favourites.animationTime);
        });

    };

    static renderFavourites = (
      totalCount: number,
      favouriteProductIds: Array<string>) => {
      // update favourites count in header
      const favouritesQuantityElement: HTMLElement = element(
        '[data-container="favourites-quantity"]'
      ) as HTMLElement;

      if (favouritesQuantityElement && totalCount && totalCount > 0) {
        favouritesQuantityElement.textContent = totalCount.toString();
        show(favouritesQuantityElement, 'flex');
      }

      if (totalCount === 0) {
        hide(favouritesQuantityElement);
      }

      if (favouriteProductIds == null || favouriteProductIds.length === 0) {
        return;
      }

      const favouriteIdSet = new Set(favouriteProductIds);

      // Fill the heart for a single element if its product is a favourite.
      function markIfFavourite(candidate: Element) {
        const candidateProductId = candidate.getAttribute('data-product-favourite-id');
        if (candidateProductId == null || !favouriteIdSet.has(candidateProductId)) {
          return;
        }

        const state = candidate.querySelector('.state');
        if (state) {
          /* Animation is running so defer the add */
          setTimeout(() => {
            candidate.classList.add('icon-favourite-active');
          }, Favourites.animationTime);
        } else {
          candidate.classList.add('icon-favourite-active');
        }
      }

      // Check the node itself and all of its descendants in a single pass.
      function scanForFavourites(root: ParentNode) {
        if (root instanceof Element) {
          markIfFavourite(root);
        }
        root.querySelectorAll('[data-product-favourite-id]').forEach(markIfFavourite);
      }

      // Product lists are often injected via ajax after this runs, so keep
      // watching the DOM and mark favourites as they appear.
      if (Favourites.currentMutationObserver) {
        Favourites.currentMutationObserver.disconnect();
      }

      Favourites.currentMutationObserver = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              scanForFavourites(node as Element);
            }
          });
        });
      });

      Favourites.currentMutationObserver.observe(document.body, {
        subtree: true,
        childList: true,
      });

      // Mark everything that is already in the DOM.
      scanForFavourites(document);
    };
  }
}

export default Meds.Modules;